// Hash function for client-side hashing using Web Crypto API
export const hashString = async (str: string): Promise<string> => {
    if (typeof window !== "undefined" && window.crypto?.subtle) {
        const encoder = new TextEncoder();
        const data = encoder.encode(str.toLowerCase().trim());
        const hashBuffer = await window.crypto.subtle.digest("SHA-256", data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    }
    return str;
};

interface UserData {
    email?: string;
    phone_number?: string;
    first_name?: string;
    last_name?: string;
    country?: string;
}

interface FormData {
    name?: string;
    email?: string;
    mobileNumber?: string;
    company?: string;
    [key: string]: any;
}

interface ConversionMeta {
    formType: string;
    leadType: "business" | "job_application" | "creator_application";
    source: string;
    trafficSource?: string;
}

// Function to push enhanced conversions data to GTM
export const pushEnhancedConversionsData = (
    formData: FormData,
    conversionMeta: ConversionMeta,
    useHashing: boolean = false
) => {
    if (typeof window === "undefined") return;

    // Parse name into first and last name
    const nameParts = formData.name?.split(" ") || [];
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";
    const hostname = window.location.hostname;
    const country = hostname.endsWith(".sg") ? "SG" : "MY";

    // Format phone number (ensure it starts with +)
    let phoneNumber = formData.mobileNumber || "";
    if (phoneNumber && !phoneNumber.startsWith("+")) {
        const countryCode = country === "SG" ? "+65" : "+60";
        phoneNumber = countryCode + phoneNumber.replace(/^0/, "");
    }

    const userData: UserData = {};

    // Only add fields that have actual data
    if (formData.email) userData.email = formData.email;
    if (phoneNumber) userData.phone_number = phoneNumber;
    if (firstName) userData.first_name = firstName;
    if (lastName) userData.last_name = lastName;
    userData.country = country;

    // Push to dataLayer
    if (window.dataLayer) {
        window.dataLayer.push({
            event: "formSubmitted",
            formType: conversionMeta.formType,
            leadType: conversionMeta.leadType,
            source: conversionMeta.source,
            trafficSource: conversionMeta.trafficSource,
            leadsUserData: userData,
        });
    }
};

export const getThankYouUrl = (
    { source, leadType, formType }: ConversionMeta,
    basePath: string = "/thankyou"
) => {
    const params = new URLSearchParams({
        source,
        leadType,
        formType,
    });

    return `${basePath}?${params.toString()}`;
};

// Declare global dataLayer type
declare global {
    interface Window {
        dataLayer: any[];
    }
}
